import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ROOT_URL_MIDDLEWARE, MIDDLEWARE_BEARER_TOKEN } from "../../common/utils/apiConstants";
import { isoStrToDate } from "../../common/utils/helperFunctions";

const timeInCacheShort = 0;
const timeInCacheMedium = 1800;
const timeInCacheLong = 3600;

export const middleware = createApi({
  reducerPath: "middleware",
  baseQuery: fetchBaseQuery({
    baseUrl: ROOT_URL_MIDDLEWARE,
    prepareHeaders: (headers) => {
      headers.set("Authorization", `Bearer ${MIDDLEWARE_BEARER_TOKEN}`);
      return headers;
    }
  }),
  tagTypes: ["Addresses", "Bills", "Products", "Orders"],
  endpoints: (build) => ({
    postSendNotification: build.mutation({
      query(body) {
        let jsonBody = decodeURIComponent(body);

        return {
          url: "/alerteTeamsNotification",
          method: "POST",
          body: JSON.parse(jsonBody)
        };
      }
    }),
    putTraiterLog: build.mutation({
      query(body) {
        let jsonBody = decodeURIComponent(body);

        return {
          url: "/traiterLog",
          method: "PUT",
          body: JSON.parse(jsonBody)
        };
      }
    }),

    getServiceStatusOnDuration: build.query({
      query: (duration) => `lireServiceStatusSurPeriode/${duration}`,
      transformResponse: (response) => {
        if (response.serviceStatus.length > 0) {
          return response.serviceStatus;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),

    getLastDateErrors: build.query({
      query: () => `lireDerniereErreurParService`,
      transformResponse: (response) => {
        if (response.lastErrors.length > 0) {
          return response.lastErrors;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),

    getLocalDateTime: build.query({
      query: (duration) => `lireHeure`,
      transformResponse: (response) => {
        if (response?.dateheure != null) {
          return response.dateheure;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),

    getLastLogsByService: build.query({
      query: (duration) => `lireDerniersLogsParService/${duration}`,
      transformResponse: (response) => {
        if (response?.lastLogs != null) {
          return response.lastLogs;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),

    getAllLogs: build.query({
      query: () => `lireLogs`,
      transformResponse: (response) => {
        if (response.logs.length > 0) {
          const newData = response.logs.map((log) => ({
            ...log,
            created_at: isoStrToDate(log.created_at)
          }));

          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    getLogsCurrentDay: build.query({
      query: () => `lireLogsJourCourant`,
      transformResponse: (response) => {
        if (response.logs.length >= 0) {
          return response.logs;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    getLogsBySelectedDay: build.query({
      query: (selectedDay) => `lireLogsParJour/${selectedDay}`,
      transformResponse: (response) => {
        if (response.logs.length >= 0) {
          return response.logs;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    postGetLogsByParams: build.mutation({
      query(body) {
        let jsonBody = JSON.parse(decodeURIComponent(body));

        return {
          url: "lireLogsParCriteres",
          method: "POST",
          body: jsonBody
        };
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    })
  })
});

export const {
  usePostSendNotificationMutation,
  usePutTraiterLogMutation,
  useGetLocalDateTimeQuery,
  useGetAllLogsQuery,
  useGetLogsCurrentDayQuery,
  useGetLogsBySelectedDayQuery,
  usePostGetLogsByParamsMutation,
  useGetLastDateErrorsQuery,
  useGetLastLogsByServiceQuery,
  useGetServiceStatusOnDurationQuery
} = middleware;
